import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader as Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { APP_VERSION } from "@/config/version";

/**
 * Destino seguro pós-login: só aceita rotas do app mobile.
 * Evita open-redirect se alguém manipular o state.from.
 */
const resolveMobileReturnPath = (from: unknown): string => {
  if (typeof from === "string" && from.startsWith("/m/") && !from.startsWith("/m/login")) {
    return from;
  }
  return "/m/chat";
};

const MobileLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading: authLoading, error, isAuthenticated } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const returnPath = resolveMobileReturnPath(
    (location.state as { from?: string } | null)?.from
  );

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate(returnPath, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, returnPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!email.trim() || !password) {
      setLocalError("Preencha e-mail e senha.");
      return;
    }

    setSubmitting(true);
    const success = await login(email.trim(), password);
    setSubmitting(false);

    if (success) {
      navigate(returnPath, { replace: true });
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-border border-t-accent-blue" />
        <p className="text-[14px] text-muted-foreground">Verificando sessão...</p>
        <button
          type="button"
          onClick={() => {
            localStorage.clear();
            sessionStorage.clear();
            window.location.reload();
          }}
          className="mt-2 text-[13px] text-muted-foreground underline underline-offset-2 active:text-foreground"
        >
          Limpar sessão e tentar novamente
        </button>
      </div>
    );
  }

  const displayError = localError || error;
  const busy = submitting;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-[max(env(safe-area-inset-bottom),24px)] pt-[max(env(safe-area-inset-top),32px)]">
      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
        <div className="mb-10 flex flex-col items-center text-center">
          <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-blue shadow-lg shadow-accent-blue/25">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <h1 className="text-[26px] font-bold tracking-tight text-foreground">
            Sua<span className="text-accent-blue">Marca</span>
          </h1>
          <p className="mt-1.5 text-[14px] text-muted-foreground">
            Entre para acessar conversas e CRM
          </p>
        </div>

        {displayError && (
          <div
            role="alert"
            className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-3.5 py-3 text-[13px] text-red-600 dark:text-red-400"
          >
            {displayError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="mobile-email" className="text-[13px] font-medium text-foreground">
              E-mail
            </label>
            <input
              id="mobile-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              disabled={busy}
              className="h-12 w-full rounded-xl border border-border bg-card px-3.5 text-[16px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-accent-blue disabled:opacity-60"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="mobile-password" className="text-[13px] font-medium text-foreground">
              Senha
            </label>
            <div className="relative">
              <input
                id="mobile-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={busy}
                className="h-12 w-full rounded-xl border border-border bg-card px-3.5 pr-12 text-[16px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-accent-blue disabled:opacity-60"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                className="absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground active:bg-hover"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-accent-blue text-[15px] font-semibold text-white transition-opacity active:opacity-90 disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "Entrando..." : "Entrar"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => navigate("/forgot-password")}
          className="mt-5 self-center text-[13px] font-medium text-muted-foreground active:text-foreground"
        >
          Esqueceu sua senha?
        </button>

        <p className="mt-auto pt-10 text-center text-[11px] text-muted-foreground">
          v{APP_VERSION}
        </p>
      </div>
    </div>
  );
};

export default MobileLogin;
